/**
 * Telemetry SLO pack — the reference light language pack (pure half).
 *
 * Demonstrates the platform over machinery that already exists, with NO parser
 * change: a module carries an `slo.p95` budget aspect and an `observed.p95`
 * measurement aspect (both freeform numeric aspects), and the procedure flags
 * the module when the observed p95 breaches its budget. Registered by the host
 * (the LSP server) so it surfaces as an LSP diagnostic. Its `slo.*` inspector
 * CONTROL is the app-shell layer's half (React) — see the app-shell pack.
 *
 * The procedure has zero false positives on a model that carries neither aspect
 * (both must be present), so registering it globally is harmless.
 */
import type { Diagnostic } from "../types.js";
import { numericAspect, type LanguagePack } from "../language-pack.js";

export const sloLanguagePack: LanguagePack = {
  id: "telemetry.slo",
  procedures: [
    {
      id: "slo/observed-p95-breaches-budget",
      aspectKey: "slo",
      run({ universe }) {
        const out: Diagnostic[] = [];
        for (const node of universe.nodes) {
          const budget = numericAspect(node, "slo.p95");
          const observed = numericAspect(node, "observed.p95");
          if (budget === undefined || observed === undefined) continue;
          if (observed > budget) {
            out.push({
              severity: "warning",
              code: "SLO_P95_BREACH",
              message: `${node.key}: observed p95 ${observed}ms exceeds SLO budget slo.p95 ${budget}ms`,
              source: node.source,
            });
          }
        }
        return out;
      },
    },
  ],
  docs: [
    {
      key: "slo.p95",
      summary: "Target p95 latency budget (ms).",
      detail:
        "The 95th-percentile latency this element commits to. The telemetry pack flags it when the `observed.p95` measurement exceeds this budget.",
    },
    {
      key: "observed.p95",
      summary: "Measured p95 latency (ms).",
      detail: "The last observed 95th-percentile latency, compared against `slo.p95`.",
    },
  ],
};
